import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/form-controls";
import { Modal } from "@/components/ui/modal";
import { CodeBlock } from "@/components/ui/console";
import { toast } from "@/components/ui/toast";
import { LOCAL_ONLY_NOTICE } from "@/lib/fixture-notice";
import { usePermission } from "@/hooks/use-permission";
import {
  MOCK_WEBHOOK_ENDPOINTS,
  DEV_WEBHOOK_EVENTS,
  DEV_WEBHOOK_DEFAULT_EVENTS,
  MOCK_WEBHOOK_DELIVERIES,
  DEV_WEBHOOK_PAYLOAD_SAMPLE,
  DEV_WEBHOOK_DELIVERY_PAYLOAD,
} from "@/data/mock";
import type { WebhookEndpoint, WebhookDelivery } from "@/types";

/* DEV-02 — Webhooks.
   Canonical source: prototype/pages/developer/webhooks.html (no
   current Figma frame located). The webhook payload sample's
   `from_wallet` field is substituted per the wallet-id hygiene
   decision documented in data/mock.ts's Developer header comment.
   Native `confirm()` for Delete is replaced with the app's Modal
   component, per the RCV-03 precedent. */

export function WebhooksPage() {
  const canManage = usePermission("service-account:manage");
  const [endpoints, setEndpoints] = useState<WebhookEndpoint[]>(MOCK_WEBHOOK_ENDPOINTS);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>(MOCK_WEBHOOK_DELIVERIES);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<WebhookEndpoint | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WebhookEndpoint | null>(null);
  const [payloadOpen, setPayloadOpen] = useState(false);

  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<Record<string, boolean>>(Object.fromEntries(DEV_WEBHOOK_EVENTS.map((e) => [e, DEV_WEBHOOK_DEFAULT_EVENTS.includes(e)])));
  const [secret, setSecret] = useState("");

  const activeCount = endpoints.filter((e) => e.status === "Active").length;
  const disabledCount = endpoints.filter((e) => e.status === "Disabled").length;

  function openAdd() {
    setEditing(null);
    setUrl("");
    setSecret("");
    setEvents(Object.fromEntries(DEV_WEBHOOK_EVENTS.map((e) => [e, DEV_WEBHOOK_DEFAULT_EVENTS.includes(e)])));
    setAddOpen(true);
  }

  function openEdit(ep: WebhookEndpoint) {
    setEditing(ep);
    setUrl(ep.url.startsWith("http") ? ep.url : `https://${ep.url}`);
    setSecret("");
    setAddOpen(true);
  }

  function saveEndpoint() {
    if (!canManage) return;
    const trimmed = url.trim();
    if (!trimmed) {
      toast({ variant: "error", title: "Enter an endpoint URL." });
      return;
    }
    const normalized = trimmed.replace(/^https?:\/\//i, "");

    if (editing) {
      setEndpoints((prev) => prev.map((e) => (e.id === editing.id ? { ...e, url: normalized } : e)));
      toast({ variant: "success", title: "Endpoint updated.", description: LOCAL_ONLY_NOTICE });
    } else {
      const fresh: WebhookEndpoint = {
        id: `wh-${Date.now()}`,
        url: normalized,
        events: "transfer.*",
        status: "Active",
        lastDeliveryOk: true,
        lastDeliveryCode: 0,
        lastDeliveryWhen: "—",
      };
      setEndpoints((prev) => [...prev, fresh]);
      toast({ variant: "success", title: "Endpoint added. A test delivery will be sent.", description: LOCAL_ONLY_NOTICE });
    }
    setAddOpen(false);
  }

  function toggleEndpoint(ep: WebhookEndpoint) {
    if (!canManage) return;
    const next: WebhookEndpoint["status"] = ep.status === "Active" ? "Disabled" : "Active";
    setEndpoints((prev) => prev.map((e) => (e.id === ep.id ? { ...e, status: next } : e)));
    toast({ variant: "success", title: `Endpoint ${next === "Disabled" ? "disabled" : "enabled"}.`, description: LOCAL_ONLY_NOTICE });
  }

  function doDelete() {
    if (!canManage || !deleteTarget) return;
    setEndpoints((prev) => prev.filter((e) => e.id !== deleteTarget.id));
    toast({ variant: "success", title: "Endpoint deleted.", description: LOCAL_ONLY_NOTICE });
    setDeleteTarget(null);
  }

  function retryDelivery(index: number) {
    if (!canManage) return;
    setDeliveries((prev) => prev.map((d, i) => (i === index ? { ...d, statusCode: 0, ok: true, retryable: false } : d)));
    toast({ variant: "success", title: "Retry sent — delivery requeued.", description: LOCAL_ONLY_NOTICE });
  }

  return (
    <div>
      <Link to="/developer" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted hover:text-primary">← Back to Developers</Link>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-ink">Webhooks</h1>
          <p className="text-sm text-muted">{activeCount} active endpoints · {disabledCount} disabled</p>
        </div>
        <Button onClick={openAdd} disabled={!canManage}>+ Add Endpoint</Button>
      </div>

      <div className="mb-7">
        <div className="mb-3 text-base font-bold text-ink">Endpoints</div>
        <div className="overflow-hidden rounded-xl border border-line bg-surface">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-muted">
                  <th className="px-5 py-2.5">URL</th>
                  <th className="px-3 py-2.5">Events</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Last Delivery</th>
                  <th className="px-5 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {endpoints.map((ep) => (
                  <tr key={ep.id} className={ep.status === "Disabled" ? "opacity-65" : ""}>
                    <td className={`px-5 py-2.5 font-mono text-xs ${ep.status === "Disabled" ? "text-subtle" : "text-ink"}`}>{ep.url}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-muted">{ep.events}</td>
                    <td className="px-3 py-2.5">
                      <Badge variant={ep.status === "Active" ? "success" : "neutral"} size="sm">{ep.status}</Badge>
                    </td>
                    <td className="px-3 py-2.5 text-xs">
                      <span className={ep.lastDeliveryOk ? "font-bold text-success" : "font-bold text-danger"}>
                        {ep.lastDeliveryOk ? "✓" : "✗"} {ep.lastDeliveryCode}
                      </span>
                      <span className="ml-1.5 text-muted">{ep.lastDeliveryWhen}</span>
                    </td>
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-2.5">
                        {ep.status === "Active" && (
                          <button type="button" className="text-xs font-medium text-primary hover:underline" onClick={() => openEdit(ep)}>Edit</button>
                        )}
                        <button type="button" className="text-xs font-medium text-muted hover:underline" onClick={() => toggleEndpoint(ep)}>
                          {ep.status === "Active" ? "Disable" : "Enable"}
                        </button>
                        <button type="button" className="text-xs font-medium text-danger hover:underline" onClick={() => setDeleteTarget(ep)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-3 text-base font-bold text-ink">Recent Deliveries</div>
        <div className="mb-6 overflow-hidden rounded-xl border border-line bg-surface">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-muted">
                  <th className="px-5 py-2.5">Event</th>
                  <th className="px-3 py-2.5">Endpoint</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Latency</th>
                  <th className="px-3 py-2.5">Timestamp</th>
                  <th className="px-5 py-2.5">Response</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {deliveries.map((d, i) => (
                  <tr key={i} className={!d.ok ? "bg-danger-soft" : ""}>
                    <td className={`px-5 py-2.5 font-mono text-xs ${d.ok ? "text-ink" : "text-subtle"}`}>{d.event}</td>
                    <td className="px-3 py-2.5 text-xs text-muted">{d.endpoint}</td>
                    <td className={`px-3 py-2.5 font-bold ${d.ok ? "text-success" : "text-danger"}`}>{d.statusCode === 0 ? "Queued" : d.statusCode}</td>
                    <td className="px-3 py-2.5 text-muted">{d.latencyMs ? `${d.latencyMs}ms` : "—"}</td>
                    <td className="px-3 py-2.5 text-muted">{d.timestamp}</td>
                    <td className="px-5 py-2.5">
                      {d.retryable ? (
                        <button type="button" className="text-xs font-medium text-primary hover:underline" onClick={() => retryDelivery(i)}>Retry</button>
                      ) : (
                        <button type="button" className="text-xs font-medium text-primary hover:underline" onClick={() => setPayloadOpen(true)}>View</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mb-2.5 text-sm font-bold text-ink">Event Payload Sample</div>
        <CodeBlock code={DEV_WEBHOOK_PAYLOAD_SAMPLE} />
      </div>

      <Modal
        open={addOpen}
        onOpenChange={setAddOpen}
        title={editing ? "Edit Webhook Endpoint" : "Add Webhook Endpoint"}
        description={editing ? "Update this endpoint URL. Changes apply to future deliveries." : "Alloy will POST JSON payloads to this URL when events occur."}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={saveEndpoint} disabled={!canManage}>{editing ? "Save" : "Add Endpoint"}</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted" htmlFor="wh-url">Endpoint URL</label>
            <input
              id="wh-url"
              type="url"
              placeholder="https://your-server.com/webhook"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
            />
          </div>
          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Events to Subscribe</div>
            <div className="grid max-h-[200px] grid-cols-2 gap-1.5 overflow-y-auto">
              {DEV_WEBHOOK_EVENTS.map((e) => (
                <label key={e} className="flex items-center gap-2 rounded-md border border-line px-2 py-1.5 font-mono text-[11px] text-ink">
                  <Checkbox checked={!!events[e]} onCheckedChange={(v) => setEvents((prev) => ({ ...prev, [e]: v }))} ariaLabel={e} />
                  {e}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted" htmlFor="wh-secret">Signing Secret (optional)</label>
            <input
              id="wh-secret"
              type="text"
              placeholder="whsec_... (auto-generated if empty)"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete this endpoint?"
        description="All queued deliveries will be cancelled."
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} disabled={!canManage}>Delete Endpoint</Button>
          </>
        }
      >
        <p className="text-sm text-muted">This action cannot be undone.</p>
      </Modal>

      <Modal
        open={payloadOpen}
        onOpenChange={setPayloadOpen}
        title="Delivery Payload"
        description="POST → api.harbortrust.com/alloy/webhooks · 200 OK · 42ms"
        size="lg"
        footer={<Button variant="ghost" onClick={() => setPayloadOpen(false)}>Close</Button>}
      >
        <CodeBlock code={DEV_WEBHOOK_DELIVERY_PAYLOAD} />
      </Modal>
    </div>
  );
}
